import { surveySentiment, type ChatReaction } from './chatExperienceSurvey';

/** One survey answer as the admin Trends endpoint returns it. */
export type ChatSurveyTrendRow = {
  id: string;
  createdAt: string;
  mode: string | null;
  modelKey: string | null;
  provider: string | null;
  subprovider: string | null;
  rating: number | null;
  reaction: ChatReaction | null;
  skipped: boolean;
  assistantReplyCount: number | null;
  totalCostUsd: number | null;
  chapterCount: number | null;
  storyBibleEntries: number | null;
  responseMs: number | null;
};

export type TrendInterval = 'hour' | 'day' | 'week' | 'month';

/**
 * Most answers Trends can open in the Responses table at once. Covers any single slice
 * or a week of rated answers, and keeps the ID list well under Postgres's bind-parameter limit.
 */
export const MAX_DRILL_IDS = 5_000;
export type TrendMeasure = 'net' | 'like' | 'fine' | 'dislike' | 'responseMs';

export type TrendStats = {
  total: number;
  skipped: number;
  answered: number;
  like: number;
  fine: number;
  dislike: number;
  /** How many of the answers were star ratings, by the reaction they count as. */
  fromStars: Record<ChatReaction, number>;
  responseMs: number[];
  answeredIds: string[];
};

const UNKNOWN = 'Unknown';
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

function band(value: number | null, limits: Array<[number, string]>, last: string): string {
  if (value == null || !Number.isFinite(value)) return UNKNOWN;
  for (const [limit, label] of limits) if (value < limit) return label;
  return last;
}

type TrendField = {
  label: string;
  /** Ordered bands keep their order; other fields are ordered by answer count. */
  order?: string[];
  key: (row: ChatSurveyTrendRow) => string;
  name?: (key: string) => string;
};

export const TREND_FIELDS = {
  mode: {
    label: 'Mode',
    key: (row) => row.mode ?? UNKNOWN,
    name: (key) => (key === 'write' ? 'Write mode' : key === 'chat' ? 'Chat mode' : key),
  },
  provider: { label: 'Provider', key: (row) => row.provider ?? UNKNOWN },
  subprovider: {
    label: 'Subprovider',
    // Only OpenRouter picks a host per request; the others always route to themselves.
    key: (row) => {
      if (!row.provider) return UNKNOWN;
      return row.provider === 'OpenRouter' ? `OpenRouter › ${row.subprovider ?? UNKNOWN}` : `${row.provider} (direct)`;
    },
  },
  model: { label: 'Model', key: (row) => row.modelKey ?? UNKNOWN },
  chatLength: {
    label: 'Chat length',
    order: ['≤5 replies', '6–10 replies', '11–20 replies', '21–40 replies', '41+ replies', UNKNOWN],
    key: (row) => band(row.assistantReplyCount, [[6, '≤5 replies'], [11, '6–10 replies'], [21, '11–20 replies'], [41, '21–40 replies']], '41+ replies'),
  },
  responseTime: {
    label: 'Response time',
    order: ['Under 15s', '15–30s', '30–60s', '1–2 min', '2 min+', UNKNOWN],
    key: (row) => band(row.responseMs == null ? null : row.responseMs / 1000, [[15, 'Under 15s'], [30, '15–30s'], [60, '30–60s'], [120, '1–2 min']], '2 min+'),
  },
  costPerReply: {
    label: 'Cost/reply',
    order: ['Under 1¢', '1–2¢', '2–5¢', '5–10¢', '10¢+', UNKNOWN],
    key: (row) => {
      if (row.totalCostUsd == null || !row.assistantReplyCount) return UNKNOWN;
      const cents = (row.totalCostUsd / row.assistantReplyCount) * 100;
      return band(cents, [[1, 'Under 1¢'], [2, '1–2¢'], [5, '2–5¢'], [10, '5–10¢']], '10¢+');
    },
  },
  chapters: {
    label: 'Chapters',
    order: ['No chapters', '1–5 chapters', '6–20 chapters', '21+ chapters', UNKNOWN],
    key: (row) => band(row.chapterCount, [[1, 'No chapters'], [6, '1–5 chapters'], [21, '6–20 chapters']], '21+ chapters'),
  },
  storyBible: {
    label: 'Story bible',
    order: ['Empty story bible', '1–5 entries', '6–20 entries', '21+ entries', UNKNOWN],
    key: (row) => band(row.storyBibleEntries, [[1, 'Empty story bible'], [6, '1–5 entries'], [21, '6–20 entries']], '21+ entries'),
  },
} satisfies Record<string, TrendField>;

export type TrendFieldId = keyof typeof TREND_FIELDS;
export const TREND_FIELD_IDS = Object.keys(TREND_FIELDS) as TrendFieldId[];

export function trendFieldName(field: TrendFieldId, key: string): string {
  const def: TrendField = TREND_FIELDS[field];
  return def.name ? def.name(key) : key;
}

export function isOrderedField(field: TrendFieldId): boolean {
  return Boolean((TREND_FIELDS[field] as TrendField).order);
}

export function emptyStats(): TrendStats {
  return { total: 0, skipped: 0, answered: 0, like: 0, fine: 0, dislike: 0, fromStars: { like: 0, fine: 0, dislike: 0 }, responseMs: [], answeredIds: [] };
}

function addRow(stats: TrendStats, row: ChatSurveyTrendRow) {
  stats.total += 1;
  if (row.responseMs != null) stats.responseMs.push(row.responseMs);
  if (row.skipped) {
    stats.skipped += 1;
    return;
  }
  const sentiment = surveySentiment(row);
  if (!sentiment) return;
  stats.answered += 1;
  stats[sentiment] += 1;
  if (!row.reaction) stats.fromStars[sentiment] += 1;
  stats.answeredIds.push(row.id);
}

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = (sorted.length - 1) / 2;
  return (sorted[Math.floor(mid)] + sorted[Math.ceil(mid)]) / 2;
}

/** Net score is % Like − % Dislike, from −100 to +100. Response time is the median in ms. */
export function measureValue(stats: TrendStats, measure: TrendMeasure): number | null {
  if (measure === 'responseMs') return median(stats.responseMs);
  if (!stats.answered) return null;
  if (measure === 'net') return ((stats.like - stats.dislike) / stats.answered) * 100;
  return (stats[measure] / stats.answered) * 100;
}

/** UTC buckets; weeks start on Monday, matching the AI Cost dashboard. */
export function bucketStart(time: number, interval: TrendInterval): number {
  if (interval === 'hour') return Math.floor(time / HOUR_MS) * HOUR_MS;
  const date = new Date(time);
  const day = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  if (interval === 'day') return day;
  if (interval === 'week') return day - ((new Date(day).getUTCDay() + 6) % 7) * DAY_MS;
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
}

export function nextBucket(time: number, interval: TrendInterval): number {
  if (interval === 'hour') return time + HOUR_MS;
  if (interval === 'day') return time + DAY_MS;
  if (interval === 'week') return time + 7 * DAY_MS;
  const date = new Date(time);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
}

export type TrendGroup = {
  key: string;
  /** One value per split field, in field order. */
  parts: string[];
  label: string;
  total: TrendStats;
  previous: TrendStats | null;
  points: TrendStats[];
};

export type ChatSurveyTrends = {
  buckets: number[];
  /** The first bucket starts before the range, so it only holds part of its period. */
  partialFirst: boolean;
  /** The last bucket ends after the range, so it is still filling in. */
  partialLast: boolean;
  overall: TrendGroup;
  groups: TrendGroup[];
};

export function buildChatSurveyTrends(
  rows: ChatSurveyTrendRow[],
  options: { start: number; end: number; previousStart: number | null; interval: TrendInterval; fields: TrendFieldId[] },
): ChatSurveyTrends {
  const { start, end, previousStart, interval, fields } = options;
  const current: ChatSurveyTrendRow[] = [];
  const previous: ChatSurveyTrendRow[] = [];
  for (const row of rows) {
    const time = Date.parse(row.createdAt);
    if (time >= start && time < end) current.push(row);
    else if (previousStart != null && time >= previousStart && time < start) previous.push(row);
  }

  const buckets: number[] = [];
  for (let time = bucketStart(start, interval); time < end; time = nextBucket(time, interval)) buckets.push(time);
  const bucketIndex = new Map(buckets.map((time, index) => [time, index]));
  const indexOf = (row: ChatSurveyTrendRow) => bucketIndex.get(bucketStart(Date.parse(row.createdAt), interval)) ?? -1;

  const keyParts = (row: ChatSurveyTrendRow) => fields.map((field) => TREND_FIELDS[field].key(row));
  const fieldOrder = fields.map((field) => {
    const def: TrendField = TREND_FIELDS[field];
    if (def.order) return def.order;
    const counts = new Map<string, number>();
    for (const row of current) counts.set(def.key(row), (counts.get(def.key(row)) ?? 0) + 1);
    return [...counts.keys()].sort((a, b) => (a === UNKNOWN ? 1 : b === UNKNOWN ? -1 : (counts.get(b) ?? 0) - (counts.get(a) ?? 0)));
  });

  const makeGroup = (key: string, parts: string[]): TrendGroup => ({
    key,
    parts,
    label: parts.length ? parts.map((part, index) => trendFieldName(fields[index], part)).join(' · ') : 'All answers',
    total: emptyStats(),
    previous: previousStart == null ? null : emptyStats(),
    points: buckets.map(() => emptyStats()),
  });
  const overall = makeGroup('all', []);
  const groups = new Map<string, TrendGroup>();
  for (const row of current) {
    const parts = keyParts(row);
    const key = parts.join('|');
    let group = groups.get(key);
    if (!group) groups.set(key, (group = makeGroup(key, parts)));
    const index = indexOf(row);
    for (const target of fields.length ? [overall, group] : [overall]) {
      addRow(target.total, row);
      if (index >= 0) addRow(target.points[index], row);
    }
  }
  for (const row of previous) {
    if (overall.previous) addRow(overall.previous, row);
    const group = groups.get(keyParts(row).join('|'));
    if (group?.previous) addRow(group.previous, row);
  }

  const rank = (group: TrendGroup) => group.parts.map((part, index) => fieldOrder[index].indexOf(part));
  const sorted = fields.length ? [...groups.values()].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    for (let i = 0; i < ra.length; i++) if (ra[i] !== rb[i]) return ra[i] - rb[i];
    return 0;
  }) : [];

  const lastEnd = buckets.length ? nextBucket(buckets[buckets.length - 1], interval) : end;
  return { buckets, partialFirst: buckets.length > 0 && buckets[0] < start, partialLast: lastEnd > end, overall, groups: sorted };
}
